import { onCleanup, splitProps, type JSX } from "solid-js"

export interface ResizeHandleProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, "onResize"> {
  direction: "horizontal" | "vertical"
  edge?: "start" | "end"
  size: number
  min: number
  max: number
  onResize: (size: number) => void
  onCollapse?: () => void
  collapseThreshold?: number
}

export function ResizeHandle(props: ResizeHandleProps) {
  const [local, rest] = splitProps(props, [
    "direction",
    "edge",
    "size",
    "min",
    "max",
    "onResize",
    "onCollapse",
    "collapseThreshold",
    "class",
    "classList",
  ])
  let stopDrag: (() => void) | undefined

  onCleanup(() => stopDrag?.())

  const handlePointerDown = (e: PointerEvent) => {
    e.preventDefault()
    stopDrag?.()
    const edge = local.edge ?? (local.direction === "vertical" ? "start" : "end")
    const start = local.direction === "horizontal" ? e.clientX : e.clientY
    const startSize = local.size
    let current = startSize
    const bodyStyle = {
      cursor: document.body.style.cursor,
      overflow: document.body.style.overflow,
      userSelect: document.body.style.userSelect,
    }

    document.body.style.cursor = local.direction === "horizontal" ? "col-resize" : "row-resize"
    document.body.style.userSelect = "none"
    document.body.style.overflow = "hidden"
    const target = e.currentTarget as HTMLDivElement
    target.setPointerCapture?.(e.pointerId)

    const onPointerUp = () => {
      stopDrag?.()
    }
    const onBlur = () => {
      stopDrag?.()
    }
    const onVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return
      stopDrag?.()
    }
    const onPointerMove = (moveEvent: PointerEvent) => {
      if (moveEvent.buttons === 0) {
        stopDrag?.()
        return
      }
      moveEvent.preventDefault()
      const pos = local.direction === "horizontal" ? moveEvent.clientX : moveEvent.clientY
      const delta =
        local.direction === "vertical"
          ? edge === "end"
            ? pos - start
            : start - pos
          : edge === "start"
            ? start - pos
            : pos - start
      current = startSize + delta
      const clamped = Math.min(local.max, Math.max(local.min, current))
      local.onResize(clamped)
    }

    stopDrag = () => {
      document.body.style.cursor = bodyStyle.cursor
      document.body.style.userSelect = bodyStyle.userSelect
      document.body.style.overflow = bodyStyle.overflow
      if (target.hasPointerCapture?.(e.pointerId)) target.releasePointerCapture(e.pointerId)
      document.removeEventListener("pointermove", onPointerMove)
      document.removeEventListener("pointerup", onPointerUp)
      document.removeEventListener("pointercancel", onPointerUp)
      window.removeEventListener("blur", onBlur)
      document.removeEventListener("visibilitychange", onVisibilityChange)
      stopDrag = undefined

      const threshold = local.collapseThreshold ?? 0
      if (local.onCollapse && threshold > 0 && current < threshold) {
        local.onCollapse()
      }
    }

    document.addEventListener("pointermove", onPointerMove)
    document.addEventListener("pointerup", onPointerUp)
    document.addEventListener("pointercancel", onPointerUp)
    window.addEventListener("blur", onBlur)
    document.addEventListener("visibilitychange", onVisibilityChange)
  }

  return (
    <div
      {...rest}
      data-component="resize-handle"
      data-direction={local.direction}
      data-edge={local.edge ?? (local.direction === "vertical" ? "start" : "end")}
      classList={{
        ...local.classList,
        [local.class ?? ""]: !!local.class,
      }}
      onPointerDown={handlePointerDown}
    />
  )
}
